/**
 * Bootstrap - Starter Kit
 *
 * @author Prismify
 * @version 1.0.6
 */

// Only the Bootstrap plugins the theme uses (data-bs-toggle="modal|collapse|offcanvas|pill"
// and `new bootstrap.Modal` in pages/index.htm). Dropdown, tooltip and popover would also need Popper.
import Modal from 'bootstrap/js/dist/modal';
import Collapse from 'bootstrap/js/dist/collapse';
import Offcanvas from 'bootstrap/js/dist/offcanvas';
import Tab from 'bootstrap/js/dist/tab';
window.bootstrap = { Modal, Collapse, Offcanvas, Tab };

import Swiper from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';
Swiper.use([Navigation, Pagination]);
window.Swiper = Swiper;

// Swiper styles: compiled into app.css (sources/scss/app.scss), not injected from here

/**
 * Sliders declared in the markup: <div class="swiper" data-swiper='{"slidesPerView": 1, ...}'>
 *
 * The attribute holds the Swiper options as JSON, plus two shortcuts:
 * - "navigation": true uses the .swiper-btn-next / .swiper-btn-prev of this slider,
 * - "pagination": true uses its .swiper-pagination.
 * They are looked up in the closest [data-swiper-scope] (when the arrows sit outside the
 * .swiper element), or in the slider itself, so several sliders on a page never share arrows.
 * The scope gets the "has-nav" class while the arrows are useful (more slides than visible).
 */
function initSliders() {
    document.querySelectorAll('[data-swiper]').forEach((element) => {
        if (element.swiper) {
            return;
        }

        const options = JSON.parse(element.dataset.swiper || '{}');
        const scope = element.closest('[data-swiper-scope]') || element;

        if (options.navigation === true) {
            options.navigation = {
                nextEl: scope.querySelector('.swiper-btn-next'),
                prevEl: scope.querySelector('.swiper-btn-prev'),
            };
        }
        if (options.pagination === true) {
            options.pagination = { el: scope.querySelector('.swiper-pagination'), clickable: true };
        }

        const toggleNavSpace = (swiper) => scope.classList.toggle('has-nav', !swiper.isLocked);
        options.on = { afterInit: toggleNavSpace, lock: toggleNavSpace, unlock: toggleNavSpace };

        new Swiper(element, options);
    });
}

document.addEventListener('DOMContentLoaded', initSliders);

/**
 * Autoplay videos (video[data-lazy-video]) only download once they get close to the viewport:
 * their <source> carries data-src instead of src until then.
 */
function loadLazyVideo(video) {
    video.querySelectorAll('source[data-src]').forEach((source) => {
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
    });
    video.load();
    if (video.hasAttribute('data-autoplay')) {
        video.play().catch(() => {});
    }
}

document.addEventListener('DOMContentLoaded', () => {
    const videos = document.querySelectorAll('video[data-lazy-video]');
    if (!('IntersectionObserver' in window)) {
        videos.forEach(loadLazyVideo);
        return;
    }

    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                observer.unobserve(entry.target);
                loadLazyVideo(entry.target);
            }
        });
    }, { rootMargin: '300px 0px' });

    videos.forEach((video) => observer.observe(video));
});

/**
 * Elfsight widgets (YouTube gallery): platform.js is only loaded once a widget gets close
 * to the viewport, and only once per page however many widgets it holds.
 */
function loadElfsight() {
    if (document.querySelector('script[data-elfsight-platform]')) {
        return;
    }
    const script = document.createElement('script');
    script.src = 'https://elfsightcdn.com/platform.js';
    script.async = true;
    script.dataset.elfsightPlatform = '';
    document.body.appendChild(script);
}

document.addEventListener('DOMContentLoaded', () => {
    const widgets = document.querySelectorAll('[data-elfsight-lazy-load]');
    if (!widgets.length) {
        return;
    }
    if (!('IntersectionObserver' in window)) {
        loadElfsight();
        return;
    }

    const observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
            observer.disconnect();
            loadElfsight();
        }
    }, { rootMargin: '600px 0px' });

    widgets.forEach((widget) => observer.observe(widget));
});

/**
 * The reCAPTCHA response field is injected by Google inside the contact form, which carries
 * WebMCP annotations: give it a description too, or the form schema is flagged incomplete.
 */
document.addEventListener('DOMContentLoaded', () => {
    if (!('MutationObserver' in window)) {
        return;
    }

    document.querySelectorAll('form[toolname]').forEach((form) => {
        const describe = () => form.querySelectorAll('textarea.g-recaptcha-response:not([toolparamdescription])').forEach((field) => {
            field.setAttribute('toolparamdescription', 'Jeton anti-spam reCAPTCHA rempli automatiquement par Google, ne pas modifier.');
        });
        describe();
        new MutationObserver(describe).observe(form, { childList: true, subtree: true });
    });
});

